import { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Alert, Tabs, DatePicker, Button, Empty, Tag, Space, Table, List, Radio,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import dayjs from 'dayjs';
import { vppReview } from '../../../services/api';

/**
 * 交易复盘：结算偏差管理 + 日/月复盘报告
 * 数据口径标注：
 *  - 预测偏差：predictions 表逐时预测 vs 遥测实际值，MAPE 为 null 表示样本不足，不显示精度数字；
 *  - 出清/结算偏差：交易闭环未接入电力交易中心，出清与结算为模拟口径，页面以「模拟」Tag 明示；
 *  - 需求响应：复用 dr_assessments 考核数据（真实遥测考核）。
 */

// MAPE 展示：null 时诚实显示样本不足
function MapeStatistic({ title, mape, sampleHours }) {
  if (mape == null) {
    return <Statistic title={title} value="样本不足" valueStyle={{ fontSize: 16, color: '#999' }} />;
  }
  return (
    <Space direction="vertical" size={4}>
      <Statistic title={title} value={mape} suffix="%" precision={2} />
      <span style={{ color: '#52c41a' }}>准确率约 {(100 - mape).toFixed(2)}%（{sampleHours} 小时样本）</span>
    </Space>
  );
}

// 预测 vs 实际偏差曲线（折线：预测/实际；柱：偏差率，右轴）
function ForecastDeviationChart({ deviation }) {
  const hours = (deviation.hourly || []).map((p) => `${p.hour}:00`);
  const option = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['预测功率', '实际功率', '偏差率'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: hours },
    yAxis: [
      { type: 'value', name: 'kW' },
      { type: 'value', name: '偏差%', axisLabel: { formatter: '{value}%' } },
    ],
    series: [
      {
        name: '预测功率', type: 'line', smooth: true, showSymbol: false,
        data: (deviation.hourly || []).map((p) => p.predicted),
        itemStyle: { color: '#1890ff' },
      },
      {
        name: '实际功率', type: 'line', smooth: true, showSymbol: false,
        data: (deviation.hourly || []).map((p) => p.actual),
        itemStyle: { color: '#fa8c16' },
      },
      {
        name: '偏差率', type: 'bar', yAxisIndex: 1,
        data: (deviation.hourly || []).map((p) => p.deviation_pct),
        itemStyle: { color: '#ccc' },
      },
    ],
  };
  return <ReactEChartsCore option={option} style={{ height: 320 }} notMerge />;
}

// 逐小时预测偏差明细表
const hourlyColumns = [
  { title: '时刻', dataIndex: 'hour', render: (h) => `${h}:00`, width: 80 },
  { title: '预测(kW)', dataIndex: 'predicted', render: (v) => (v == null ? '-' : v) },
  { title: '实际(kW)', dataIndex: 'actual', render: (v) => (v == null ? '-' : v) },
  {
    title: '偏差(kW)', dataIndex: 'deviation',
    render: (v) => (v == null ? '-' : <span style={{ color: v > 0 ? '#fa8c16' : '#1890ff' }}>{v > 0 ? `+${v}` : v}</span>),
  },
  {
    title: '偏差率', dataIndex: 'deviation_pct',
    render: (v) => (v == null ? '-' : `${v}%`),
  },
];

// 出清偏差明细表（每行一个交易周期）
const clearingColumns = [
  { title: '交易周期', dataIndex: 'cycle_id', width: 200 },
  { title: '市场', dataIndex: 'market_type', width: 90 },
  { title: '申报量(MWh)', dataIndex: ['totals', 'declared_mwh'] },
  { title: '出清量(MWh)', dataIndex: ['totals', 'cleared_mwh'] },
  {
    title: '电量偏差(MWh)', dataIndex: ['totals', 'deviation_mwh'],
    render: (v, r) => (v == null ? '-' : `${v}（${r.totals.deviation_pct ?? '-'}%）`),
  },
  {
    title: '均价偏差(元/kWh)', dataIndex: ['totals', 'avg_price_deviation'],
    render: (v) => (v == null ? '-' : v),
  },
  { title: '出清金额(元)', dataIndex: ['totals', 'clearing_amount'] },
  { title: '结算总额(元)', dataIndex: ['totals', 'settled_revenue'], render: (v) => (v == null ? '-' : v) },
  {
    title: '口径', dataIndex: 'simulated', width: 80,
    render: (s) => (s ? <Tag color="orange">模拟</Tag> : <Tag color="green">真实</Tag>),
  },
];

// 需求响应考核偏差明细表
const drColumns = [
  { title: '事件编号', dataIndex: 'event_no', width: 160 },
  { title: '事件', dataIndex: 'title' },
  { title: '目标(kW)', dataIndex: 'target_power_kw' },
  { title: '实际响应(kW)', dataIndex: 'response_kw' },
  {
    title: '偏差(kW)', dataIndex: 'deviation_kw',
    render: (v) => <span style={{ color: v >= 0 ? '#52c41a' : '#f5222d' }}>{v > 0 ? `+${v}` : v}</span>,
  },
  { title: '响应准确率', dataIndex: 'accuracy_rate', render: (v) => `${v}%` },
  {
    title: '考核结果', dataIndex: 'result',
    render: (r) => <Tag color={r === '达标' ? 'green' : 'red'}>{r}</Tag>,
  },
  { title: '补贴(元)', dataIndex: 'subsidy_amount' },
];

// 偏差归因摘要
function AttributionList({ attribution }) {
  const items = [];
  for (const f of attribution?.forecast_worst_hours || []) {
    items.push(
      `预测偏差：${f.date ? `${f.date} ` : ''}${f.hour}:00 ${f.type === 'load' ? '负荷' : '光伏'}${f.direction} ${Math.abs(f.deviation_pct)}%（预测 ${f.predicted} / 实际 ${f.actual} kW）`,
    );
  }
  for (const p of attribution?.price_deviation_hours || []) {
    items.push(
      `报价偏差：${p.date ? `${p.date} ` : ''}${p.hour}:00 周期 ${p.cycle_id} ${p.direction} ${Math.abs(p.price_deviation)} 元/kWh（报价 ${p.declared_price} / 出清 ${p.cleared_price}）`,
    );
  }
  if (items.length === 0) return <Empty description="暂无可归因的偏差数据" />;
  return <List size="small" dataSource={items} renderItem={(item) => <List.Item>{item}</List.Item>} />;
}

// ============ 日复盘 ============
function DailyPanel() {
  const [date, setDate] = useState(dayjs());
  const [report, setReport] = useState(null);
  const [forecastDev, setForecastDev] = useState(null); // 当前选中类型的预测偏差曲线数据
  const [devType, setDevType] = useState('load');
  const [clearing, setClearing] = useState(null);
  const [dr, setDr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d, type) => {
    setLoading(true);
    try {
      const dateStr = d.format('YYYY-MM-DD');
      const [reportRes, devRes, clearingRes, drRes] = await Promise.all([
        vppReview.daily({ date: dateStr }),
        vppReview.forecastDeviation({ date: dateStr, type }),
        vppReview.clearingDeviation({ date: dateStr }),
        vppReview.demandDeviation({ date: dateStr }),
      ]);
      setReport(reportRes.data || null);
      setForecastDev(devRes.data || null);
      setClearing(clearingRes.data || null);
      setDr(drRes.data || null);
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(dayjs(), 'load'); }, [load]);

  const handleTypeChange = (e) => {
    const type = e.target.value;
    setDevType(type);
    // 切换负荷/光伏只重查偏差曲线，报告其余部分不变
    vppReview.forecastDeviation({ date: date.format('YYYY-MM-DD'), type })
      .then((res) => setForecastDev(res.data || null))
      .catch(() => {});
  };

  const forecastSummary = report?.forecast || {};
  const hasDeviationCurve = forecastDev && (forecastDev.hourly || []).length > 0;

  return (
    <div>
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={(
          <Space>
            日复盘报告
            {report?.simulated && <Tag color="orange">出清/结算为模拟口径</Tag>}
          </Space>
        )}
        extra={(
          <Space>
            <DatePicker
              value={date}
              allowClear={false}
              disabledDate={(cur) => cur && cur.isAfter(dayjs().endOf('day'))}
              onChange={(d) => { if (d) { setDate(d); load(d, devType); } }}
            />
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(date, devType)}>刷新</Button>
          </Space>
        )}
      >
        {!report && !loading && <Empty description="暂无复盘数据" />}
        {report && (
          <>
            <Alert
              style={{ marginBottom: 16 }}
              type="info"
              showIcon
              message={report.note}
            />
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Card size="small">
                  <MapeStatistic title="负荷预测 MAPE" mape={forecastSummary.load?.mape} sampleHours={forecastSummary.load?.sample_hours} />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <MapeStatistic title="光伏预测 MAPE" mape={forecastSummary.pv?.mape} sampleHours={forecastSummary.pv?.sample_hours} />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="出清量偏差(MWh)"
                    value={report.clearing?.totals?.deviation_mwh ?? '-'}
                    suffix={report.clearing?.totals?.deviation_pct != null ? `(${report.clearing.totals.deviation_pct}%)` : ''}
                  />
                  <span style={{ color: '#999', fontSize: 12 }}>当日交易周期 {report.clearing?.cycle_count ?? 0} 个</span>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  {report.demand_response?.pass_rate != null ? (
                    <Statistic title="需求响应达标率" value={report.demand_response.pass_rate} suffix="%" precision={2} />
                  ) : (
                    <Statistic title="需求响应达标率" value="样本不足" valueStyle={{ fontSize: 16, color: '#999' }} />
                  )}
                  <span style={{ color: '#999', fontSize: 12 }}>补贴合计 {report.demand_response?.total_subsidy ?? 0} 元</span>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Card>

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title="预测 vs 实际 偏差曲线"
        extra={(
          <Radio.Group value={devType} onChange={handleTypeChange} size="small">
            <Radio.Button value="load">负荷</Radio.Button>
            <Radio.Button value="pv">光伏</Radio.Button>
          </Radio.Group>
        )}
      >
        {forecastDev?.note && <Alert style={{ marginBottom: 12 }} type="warning" showIcon message={forecastDev.note} />}
        {hasDeviationCurve ? (
          <>
            <ForecastDeviationChart deviation={forecastDev} />
            <Table
              style={{ marginTop: 16 }}
              size="small"
              rowKey="hour"
              columns={hourlyColumns}
              dataSource={forecastDev.hourly}
              pagination={false}
              scroll={{ y: 320 }}
            />
          </>
        ) : (
          !loading && <Empty description="当日无预测偏差数据（无落库预测或无实际遥测）" />
        )}
      </Card>

      <Card size="small" style={{ marginBottom: 16 }} title="出清 vs 申报/结算 偏差">
        {clearing?.note && clearing.cycle_count === 0 && (
          <Alert style={{ marginBottom: 12 }} type="info" showIcon message={clearing.note} />
        )}
        <Table
          size="small"
          rowKey="cycle_id"
          columns={clearingColumns}
          dataSource={clearing?.cycles || []}
          pagination={false}
          locale={{ emptyText: '当日无已出清的交易周期' }}
        />
      </Card>

      <Card size="small" style={{ marginBottom: 16 }} title="需求响应考核偏差">
        <Table
          size="small"
          rowKey="event_id"
          columns={drColumns}
          dataSource={dr?.items || []}
          pagination={false}
          locale={{ emptyText: '当日无已考核的需求响应事件' }}
        />
      </Card>

      <Card size="small" title="偏差归因摘要">
        <AttributionList attribution={report?.attribution} />
      </Card>
    </div>
  );
}

// ============ 月复盘 ============
function MonthlyPanel() {
  const [month, setMonth] = useState(dayjs());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await vppReview.monthly({ month: m.format('YYYY-MM') });
      setReport(res.data || null);
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(dayjs()); }, [load]);

  const daily = report?.daily || [];
  const chartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['负荷MAPE', '光伏MAPE', '出清偏差'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: daily.map((d) => d.date.slice(8)) },
    yAxis: [
      { type: 'value', name: 'MAPE%', axisLabel: { formatter: '{value}%' } },
      { type: 'value', name: 'MWh' },
    ],
    series: [
      { name: '负荷MAPE', type: 'line', smooth: true, showSymbol: false, data: daily.map((d) => d.load_mape), itemStyle: { color: '#1890ff' }, connectNulls: false },
      { name: '光伏MAPE', type: 'line', smooth: true, showSymbol: false, data: daily.map((d) => d.pv_mape), itemStyle: { color: '#fa8c16' }, connectNulls: false },
      { name: '出清偏差', type: 'bar', yAxisIndex: 1, data: daily.map((d) => d.deviation_mwh), itemStyle: { color: '#ccc' } },
    ],
  };

  const dailyColumns = [
    { title: '日期', dataIndex: 'date', width: 110 },
    { title: '负荷MAPE', dataIndex: 'load_mape', render: (v) => (v == null ? '样本不足' : `${v}%`) },
    { title: '光伏MAPE', dataIndex: 'pv_mape', render: (v) => (v == null ? '样本不足' : `${v}%`) },
    { title: '出清量(MWh)', dataIndex: 'cleared_mwh' },
    {
      title: '出清偏差(MWh)', dataIndex: 'deviation_mwh',
      render: (v) => <span style={{ color: v >= 0 ? '#52c41a' : '#f5222d' }}>{v > 0 ? `+${v}` : v}</span>,
    },
    {
      title: '需求响应考核', dataIndex: 'dr_assessed',
      render: (v, r) => (v > 0 ? `${r.dr_passed}/${v} 达标` : '-'),
    },
  ];

  return (
    <div>
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={(
          <Space>
            月复盘报告
            {report?.simulated && <Tag color="orange">出清/结算为模拟口径</Tag>}
          </Space>
        )}
        extra={(
          <Space>
            <DatePicker
              picker="month"
              value={month}
              allowClear={false}
              disabledDate={(cur) => cur && cur.isAfter(dayjs().endOf('month'))}
              onChange={(m) => { if (m) { setMonth(m); load(m); } }}
            />
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(month)}>刷新</Button>
          </Space>
        )}
      >
        {!report && !loading && <Empty description="暂无复盘数据" />}
        {report && (
          <>
            <Alert style={{ marginBottom: 16 }} type="info" showIcon message={report.note} />
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Card size="small">
                  <MapeStatistic title="全月负荷预测 MAPE" mape={report.forecast?.load?.mape} sampleHours={report.forecast?.load?.sample_hours} />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <MapeStatistic title="全月光伏预测 MAPE" mape={report.forecast?.pv?.mape} sampleHours={report.forecast?.pv?.sample_hours} />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="全月出清量偏差(MWh)"
                    value={report.clearing?.totals?.deviation_mwh ?? '-'}
                    suffix={report.clearing?.totals?.deviation_pct != null ? `(${report.clearing.totals.deviation_pct}%)` : ''}
                  />
                  <span style={{ color: '#999', fontSize: 12 }}>全月交易周期 {report.clearing?.cycle_count ?? 0} 个</span>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  {report.demand_response?.pass_rate != null ? (
                    <Statistic title="全月需求响应达标率" value={report.demand_response.pass_rate} suffix="%" precision={2} />
                  ) : (
                    <Statistic title="全月需求响应达标率" value="样本不足" valueStyle={{ fontSize: 16, color: '#999' }} />
                  )}
                  <span style={{ color: '#999', fontSize: 12 }}>补贴合计 {report.demand_response?.total_subsidy ?? 0} 元</span>
                </Card>
              </Col>
            </Row>
            <ReactEChartsCore option={chartOption} style={{ height: 320, marginTop: 16 }} notMerge />
          </>
        )}
      </Card>

      <Card size="small" style={{ marginBottom: 16 }} title="逐日明细">
        <Table
          size="small"
          rowKey="date"
          columns={dailyColumns}
          dataSource={daily}
          pagination={false}
          scroll={{ y: 400 }}
        />
      </Card>

      <Card size="small" title="偏差归因摘要（全月 Top5）">
        <AttributionList attribution={report?.attribution} />
      </Card>
    </div>
  );
}

export default function VppReview() {
  return (
    <Tabs
      items={[
        { key: 'daily', label: '日复盘', children: <DailyPanel /> },
        { key: 'monthly', label: '月复盘', children: <MonthlyPanel /> },
      ]}
    />
  );
}
